import React, { Suspense, useEffect, useState } from 'react';
import Spline from '@splinetool/react-spline';
import './RoboticHand.css';

const RoboticHandAnimation = () => {
  // Skip the heavy Spline 3D scene on mobile — it's the #1 source of jank
  // on lower-end phones. We still render the SVG overlay for a premium look.
  const [isMobile, setIsMobile] = useState(() => {
    if (typeof window === 'undefined') return false;
    return window.matchMedia('(max-width: 768px)').matches;
  });
  useEffect(() => {
    const mq = window.matchMedia('(max-width: 768px)');
    const handler = (e) => setIsMobile(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);

  return (
    <div className="robotic-hand-container-3d">
      <div className="spline-3d-wrapper">
        {/* 3D Animated Spline Scene (desktop only) */}
        {!isMobile && (
          <Suspense fallback={
            <div className="spline-loader">
              <div className="loader-spinner"></div>
            </div>
          }>
            <Spline 
              scene="https://prod.spline.design/NbVmy6DPLhY-5Lvg/scene.splinecode"
            />
          </Suspense>
        )}
        {isMobile && <div className="spline-mobile-halo" aria-hidden="true" />}
        
        {/* SVG Overlay for Words */}
        <svg 
          viewBox="0 0 500 650" 
          className="overlay-svg"
          xmlns="http://www.w3.org/2000/svg"
        >
          <defs>
            <filter id="glow">
              <feGaussianBlur stdDeviation="3" result="coloredBlur"/>
              <feMerge>
                <feMergeNode in="coloredBlur"/>
                <feMergeNode in="SourceGraphic"/>
              </feMerge>
            </filter>
            
            <linearGradient id="handGradient" x1="0%" y1="0%" x2="0%" y2="100%">
              <stop offset="0%" style={{stopColor: '#00FFD1', stopOpacity: 1}} />
              <stop offset="100%" style={{stopColor: '#00D4FF', stopOpacity: 0.9}} />
            </linearGradient>
          </defs>

          {/* Floating Words - All around KI MASTERCLASS in the center */}
          <g className="floating-words">
            {/* LEADS - Top Left */}
            <g className="word-group word-1">
              <line x1="250" y1="320" x2="100" y2="150" stroke="#00FFD1" strokeWidth="2" opacity="0.5" strokeDasharray="5,5" />
              <circle cx="100" cy="150" r="6" fill="none" stroke="#00FFD1" strokeWidth="2.5" filter="url(#glow)" />
              <circle cx="100" cy="150" r="3" fill="#00FFD1" opacity="0.8" />
              <text 
                x="100" 
                y="130" 
                fill="url(#handGradient)" 
                fontSize="24" 
                fontWeight="900" 
                textAnchor="middle"
                filter="url(#glow)"
                style={{fontFamily: 'Arial, sans-serif', letterSpacing: '3px'}}
              >
                LEADS
              </text>
            </g>

            {/* CRM - Top Right */}
            <g className="word-group word-2">
              <line x1="250" y1="320" x2="400" y2="150" stroke="#00FFD1" strokeWidth="2" opacity="0.5" strokeDasharray="5,5" />
              <circle cx="400" cy="150" r="6" fill="none" stroke="#00FFD1" strokeWidth="2.5" filter="url(#glow)" />
              <circle cx="400" cy="150" r="3" fill="#00FFD1" opacity="0.8" />
              <text 
                x="400" 
                y="130" 
                fill="url(#handGradient)" 
                fontSize="26" 
                fontWeight="900" 
                textAnchor="middle"
                filter="url(#glow)"
                style={{fontFamily: 'Arial, sans-serif', letterSpacing: '3px'}}
              >
                CRM
              </text>
            </g>

            {/* AUTOMATIONS - Left */}
            <g className="word-group word-3">
              <line x1="250" y1="320" x2="70" y2="240" stroke="#00FFD1" strokeWidth="2" opacity="0.5" strokeDasharray="5,5" />
              <circle cx="70" cy="240" r="6" fill="none" stroke="#00FFD1" strokeWidth="2.5" filter="url(#glow)" />
              <circle cx="70" cy="240" r="3" fill="#00FFD1" opacity="0.8" />
              <text 
                x="70" 
                y="220" 
                fill="url(#handGradient)" 
                fontSize="20" 
                fontWeight="900" 
                textAnchor="middle"
                filter="url(#glow)"
                style={{fontFamily: 'Arial, sans-serif', letterSpacing: '2px'}}
              >
                AUTOMATIONS
              </text>
            </g>

            {/* WHATSAPP - Bottom Center (lower) */}
            <g className="word-group word-4">
              <line x1="250" y1="320" x2="250" y2="530" stroke="#00FFD1" strokeWidth="2" opacity="0.5" strokeDasharray="5,5" />
              <circle cx="250" cy="530" r="6" fill="none" stroke="#00FFD1" strokeWidth="2.5" filter="url(#glow)" />
              <circle cx="250" cy="530" r="3" fill="#00FFD1" opacity="0.8" />
              <text 
                x="250" 
                y="555" 
                fill="url(#handGradient)" 
                fontSize="22" 
                fontWeight="900" 
                textAnchor="middle"
                filter="url(#glow)"
                style={{fontFamily: 'Arial, sans-serif', letterSpacing: '2px'}}
              >
                WHATSAPP
              </text>
            </g>

            {/* MARKETING - Bottom Left */}
            <g className="word-group word-5">
              <line x1="250" y1="320" x2="100" y2="470" stroke="#00FFD1" strokeWidth="2" opacity="0.5" strokeDasharray="5,5" />
              <circle cx="100" cy="470" r="6" fill="none" stroke="#00FFD1" strokeWidth="2.5" filter="url(#glow)" />
              <circle cx="100" cy="470" r="3" fill="#00FFD1" opacity="0.8" />
              <text 
                x="100" 
                y="495" 
                fill="url(#handGradient)" 
                fontSize="22" 
                fontWeight="900" 
                textAnchor="middle"
                filter="url(#glow)"
                style={{fontFamily: 'Arial, sans-serif', letterSpacing: '2px'}}
              >
                MARKETING
              </text>
            </g>

            {/* KI AGENT - Bottom Right */}
            <g className="word-group word-6">
              <line x1="250" y1="320" x2="400" y2="470" stroke="#00FFD1" strokeWidth="2" opacity="0.5" strokeDasharray="5,5" />
              <circle cx="400" cy="470" r="6" fill="none" stroke="#00FFD1" strokeWidth="2.5" filter="url(#glow)" />
              <circle cx="400" cy="470" r="3" fill="#00FFD1" opacity="0.8" />
              <text 
                x="400" 
                y="495" 
                fill="url(#handGradient)" 
                fontSize="22" 
                fontWeight="900" 
                textAnchor="middle"
                filter="url(#glow)"
                style={{fontFamily: 'Arial, sans-serif', letterSpacing: '2px'}}
              >
                KI AGENT
              </text>
            </g>
          </g>

          {/* KI MASTERCLASS - CENTER (The main hub) */}
          <g className="masterclass-title">
            <circle cx="250" cy="320" r="15" fill="none" stroke="#00FFD1" strokeWidth="3.5" filter="url(#glow)" className="central-hub" />
            <circle cx="250" cy="320" r="8" fill="#00FFD1" opacity="0.8" className="pulse" />
            <text 
              x="250" 
              y="305" 
              fill="url(#handGradient)" 
              fontSize="48" 
              fontWeight="900" 
              textAnchor="middle"
              filter="url(#glow)"
              className="title-text"
              style={{fontFamily: 'Arial, sans-serif', letterSpacing: '6px'}}
            >
              KI MASTERCLASS
            </text>
            <line x1="110" y1="332" x2="390" y2="332" stroke="#00FFD1" strokeWidth="4" opacity="0.7" className="title-underline" />
          </g>

          {/* Energy Particles */}
          <g className="particles">
            <circle cx="150" cy="180" r="2" fill="#00FFD1" className="particle particle-1" />
            <circle cx="350" cy="200" r="2" fill="#00FFD1" className="particle particle-2" />
            <circle cx="250" cy="160" r="2" fill="#00FFD1" className="particle particle-3" />
            <circle cx="200" cy="380" r="2" fill="#00FFD1" className="particle particle-4" />
            <circle cx="300" cy="400" r="2" fill="#00FFD1" className="particle particle-5" />
          </g>
        </svg>
      </div>
    </div>
  );
};

export default RoboticHandAnimation;
